import { z } from 'zod';

import type { SourcePos } from '../../ast.ts';
import { colNodeLabels, pickColumnPath, walkStructPath } from '../../column-ref.ts';
import {
  JoinOnShapeError,
  preferredSubfield,
  relationColumnHint,
  ShapeError,
  TypeMismatchError,
} from '../../errors.ts';
import { type AstInput, astShape, nodePos } from '../../normalize.ts';
import { opMeta, type SpecialCtx } from '../../op-meta.ts';
import { elemPath, kwargPath, paramPath } from '../../path.ts';
import { runtimeJoin } from '../../runtime/arquero.ts';
import { dedupeByKeys } from '../../runtime/partition.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { enumValueAstString, keywordOrEnumAstSchema } from '../../schema/keyword.ts';
import type { IdentityTable, RowIdentityType } from '../../schema/row-identity.ts';
import {
  type ColumnType,
  identityElement,
  nullableCol,
  replaceUnit,
  type Schema,
  scalarSubfields,
  schemaFromEntries,
  unitOfType,
  unknownCol,
  unwrapNullable,
  walkableFields,
} from '../../schema/types.ts';
import { type Cell, MaterializedDataset } from '../../value.ts';
import { resolveDsArg } from './helpers.ts';

/**
 * SSOT for the join-type domain. Both the schema validation here
 * (`keywordOrEnumAstSchema(JOIN_TYPES)`) and the arquero dispatch
 * table in `runtime/arquero.ts` import this same constant — `JoinType`
 * below is derived from it, so adding a variant requires editing only
 * this one tuple and the arquero `JOIN_FNS` dispatcher.
 */
export const JOIN_TYPES = ['inner', 'left', 'right', 'full', 'cross'] as const;

/** Join-type domain — derived from {@link JOIN_TYPES}. */
export type JoinType = (typeof JOIN_TYPES)[number];

/**
 * `(join ds-a ds-b :on [:col … | [outer inner] …] :type :inner|left|right|full|cross)` —
 * inner/left/right/full join of two datasets on key columns, or a keyless
 * `:cross` (cartesian) join.
 *
 * Two explicit positional datasets — no threaded "current" dataset.
 * Threading is composed naturally: `(-> a (join b :on [:id]))` ↔
 * `(join a b :on [:id])` (the threading rewriter inserts `a` as the
 * first positional).
 *
 * `:on` is a vector of entries; each entry is one side (self-join,
 * same column name on both sides) or a `[outer inner]` pair
 * (different key names per side). A side is a keyword
 * (`:column-name`) or a `(col [<binding>] <column> [<subfield>…])`
 * reference for sub-path keys (struct fields, or a relation's
 * `externalId`/`space`); the leading binding label in `(col …)` is
 * ignored — role decides the dataset (`outer` → `ds-a`, `inner` →
 * `ds-b`).
 *
 * A bare relation key (a direct-relation reference column) is keyed
 * by INSTANCE IDENTITY — each cell keyed via its row-identity
 * contract — so relation⋈relation entries match linked instances
 * directly. To join a relation against a scalar id column, key the
 * relation's `externalId` sub-path instead; an entry mixing an
 * identity-keyed relation with a scalar key is rejected up front
 * (the keys could never match).
 *
 * `:type` defaults to `:inner` and accepts either a keyword
 * (`:inner`) or a quoted string (`"inner"`) via the engine-wide
 * `keywordOrEnumAstSchema` leniency policy.
 *
 * `:type :cross` is a keyless cartesian product: every `ds-a` row pairs
 * with every `ds-b` row, so `:on` is omitted (supplying it is an error).
 * The common use is broadcasting a single-row summary (a `:group-by`-less
 * `aggregate`, which drops the grain key) onto every row of `ds-a`.
 *
 * Errors:
 *
 *   - Either positional not a `Dataset` → `TypeMismatchError`.
 *   - A keyed join with an empty/absent `:on`, a `:cross` join carrying
 *     `:on` keys, or a malformed side → `ShapeError`.
 *   - A keyword self-join key isn't a column on BOTH datasets →
 *     `TypeMismatchError` naming the side that's missing.
 *   - A `(col …)` key resolves to a struct without a sub-path, or
 *     names a missing struct sub-field → `TypeMismatchError`.
 *   - Struct-root columns that both sides use as a sub-path key root
 *     are de-shadowed automatically: `:inner`/`:left` keep the
 *     outer's column under its plain name, `:right` keeps the
 *     inner's, `:full` coalesces per row (outer first, inner
 *     fallback). Other non-key column-name collisions are suffixed
 *     by arquero (`name_1` / `name_2`), NOT thrown — `rename`
 *     afterwards if you need to keep both.
 *
 * Either positional may be a `LazyDataset`; both are forced via
 * `ctx.materialize` before the arquero round-trip.
 */
export const join = z
  .object({
    name: z.literal('join'),
    params: z.tuple([astArg('<ds-a>'), astArg('<ds-b>')]),
    // Optional at the schema level: a `:cross` join is keyless and omits
    // `:on` entirely. Apply enforces the coupling — `:on` required (≥1 key)
    // for keyed joins, forbidden for `:cross`.
    on: z.array(z.unknown()).optional(),
    type: keywordOrEnumAstSchema(JOIN_TYPES).optional(),
  })
  .describe(
    'Inner/left/right/full join of two datasets on key columns, or a keyless `:cross` cartesian join. Two positional datasets `ds-a` `ds-b`; `:on` is a vector of keyword keys (`:col` — self-join, same column both sides) or `[outer inner]` pairs (different-name key — outer resolves against `ds-a`, inner against `ds-b`). A side can be a `(col [<binding>] <column> [<subfield>…])` reference for struct or relation sub-path keys (e.g. `(col wellString externalId)`). A bare relation key joins by instance identity — both sides must then be relations. `:type` defaults to `:inner`; `:type :cross` omits `:on` and pairs every left row with every right row (broadcasts a single-row `ds-b` onto every `ds-a` row).',
  )
  .register(opMeta, {
    // Special evaluation: `:on` carries raw `(col …)` AST nodes whose
    // labels must be inspected structurally — eager dispatch would
    // `evalNode` them and surface unrelated errors. Both positional
    // datasets are evaluated by hand via `ctx.evalNode`.
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(join wells production :on [:well-id] :type :left)' },
    apply: async (data, ctx: SpecialCtx) => {
      const [astA, astB] = data.params as readonly [AstInput, AstInput];

      // Evaluate both positionals and resolve each to a
      // MaterializedDataset (forcing lazy operands via ctx.materialize).
      const valueA = (await ctx.evalNode(astA, ctx.env, paramPath(ctx.path, 0))).value;
      const valueB = (await ctx.evalNode(astB, ctx.env, paramPath(ctx.path, 1))).value;
      const dsA = await resolveDsArg(
        valueA,
        '(join ds-a ds-b :on […])',
        ctx,
        nodePos(ctx.spans, paramPath(ctx.path, 0)),
      );
      const dsB = await resolveDsArg(
        valueB,
        '(join ds-a ds-b :on […])',
        ctx,
        nodePos(ctx.spans, paramPath(ctx.path, 1)),
      );

      const onPath = kwargPath(ctx.path, 'on');
      const onEntries = (data.on ?? []) as readonly unknown[];
      const joinType: JoinType = data.type === undefined ? 'inner' : enumValueAstString(data.type);

      // `:on` couples to `:type`: a keyed join needs at least one key; a
      // `:cross` (keyless cartesian) join takes none. The schema keeps `:on`
      // optional so `:cross` can omit it, so the coupling is enforced here.
      if (joinType === 'cross') {
        if (onEntries.length > 0) {
          throw new ShapeError(
            '`:type :cross` is a keyless cartesian join and takes no `:on` keys',
            nodePos(ctx.spans, onPath),
            ':on',
          );
        }
      } else if (onEntries.length === 0) {
        throw new ShapeError('`:on` must list at least one key', nodePos(ctx.spans, ctx.path), ':on');
      }

      // Resolve `:on` to per-entry { left, right } key-name pairs, collecting
      // synthetic struct-key columns to drop post-join AND shadowed struct
      // roots to de-shadow per join type. A `:cross` join has no keys, so its
      // resolution is empty and both datasets pass through unaugmented.
      const resolved: ResolvedOn =
        joinType === 'cross'
          ? { keyPairs: [], augmentedA: dsA, augmentedB: dsB, syntheticKeys: new Set(), shadowedRoots: new Set() }
          : resolveOnClause(onEntries, dsA, dsB, onPath, ctx);
      const { keyPairs, augmentedA, augmentedB } = resolved;

      // Implicit right-side dedupe by join key. Right-side key duplicates
      // cause Arquero's relational join to fan out (one left row × N
      // right matches = N output rows). Across chained joins this
      // compounds multiplicatively — "the same instance appears N times
      // in the output after stacking lookups". The LLM cannot be relied
      // upon to reason about lookup-side uniqueness, so the dedupe is
      // implicit and always-on for the join types where the right side
      // is a lookup. Left cardinality is the caller's invariant and is
      // left untouched: many entities sharing one lookup row (many
      // strings → one well name) must still emit one output row per
      // left row.
      //
      // Runs AFTER `resolveOnClause` so the dedupe key matches the flat
      // scalar that the join itself runs on (handles struct-path and
      // ref-column key synthesis uniformly). Routes through the shared
      // `dedupeByKeys` SSOT (structural equality via `cellKey`), so
      // struct-valued synthetic keys collapse by content — not by JS
      // reference identity.
      //
      // Gated to :inner / :left only. :right / :full are contractually
      // required to preserve right-side cardinality (every right row
      // appears, with `null` left columns if unmatched), so deduping
      // there would silently drop documented rows.
      const rightKeys = keyPairs.map((p) => p.right);
      const dedupeRight = joinType === 'inner' || joinType === 'left';
      const dedupedAugmentedB = dedupeRight ? dedupeByKeys(augmentedB, rightKeys) : augmentedB;

      const joined = runtimeJoin(augmentedA, dedupedAugmentedB, [keyPairs.map((p) => p.left), rightKeys], joinType);

      // `runtimeJoin` → `fromTable` already ran the universal
      // value-based `refineSchema`, so the joined schema is recovered
      // from the actual cells: scalar/struct columns are concretely
      // typed (including any cell-derived column unit from boxed
      // cells) and a `nested-dataset` column is deliberately left
      // `unknown` (per `schema/refine.ts` — preserves the
      // `unnest`/`time-bucket` dispatch). `cleanupJoinOutput` then
      // drops the synthetic `__joinkey_*` columns AND de-shadows any
      // struct-root columns that both sides used as a sub-path key
      // root (so the canonical chained `(col wellString externalId)`
      // pattern leaves a plain `wellString` for the next hop).
      //
      // `typeJoinedColumns` then re-types the emitted columns from the
      // two INHERENT operand schemas (`dsA.schema` / `dsB.schema`) —
      // the authoritative typed schema that flows downstream (declared
      // kinds, struct sub-path access, type-driven formatting) — while
      // keeping the cell-refined unit. The cells and identity from
      // `cleanupJoinOutput` carry through unchanged.
      const out = cleanupJoinOutput(joined, resolved, joinType);
      return {
        value: new MaterializedDataset(out.columns, typeJoinedColumns(out, dsA, dsB, joinType, resolved), out.identity),
        env: ctx.env,
      };
    },
    dataset: {
      // Two positional datasets: the engine's `PropagateSchemaCtx`
      // carries only ds-a's schema (params[0]), so a static overlay
      // here could never type ds-b's columns or the join's null-fill
      // nullability. Returning `undefined` instead lets `evalDatasetOp`
      // preserve the schema `apply` attaches — `apply`'s
      // `typeJoinedColumns` is the authoritative source, re-typing every
      // emitted column from BOTH inherent operand schemas (recovering
      // declared types the value-based refine cannot).
      //
      // `referencedColumns` is intentionally omitted (≡ `[]`): `:on`
      // refs span TWO datasets, and `apply`'s `resolveOnClause`
      // validates every side comprehensively against the correct
      // schema.
      propagateSchema: () => undefined,
    },
  });

type OnSideShape = { readonly $kw: string } | { readonly name: 'col'; readonly params?: readonly AstInput[] };

type ResolvedOn = {
  readonly keyPairs: readonly { readonly left: string; readonly right: string }[];
  readonly augmentedA: MaterializedDataset;
  readonly augmentedB: MaterializedDataset;
  readonly syntheticKeys: ReadonlySet<string>;
  /**
   * Struct columns that BOTH sides used as the root of a sub-path key.
   * Such columns would otherwise survive the arquero join as a non-key
   * collision (`root_1` / `root_2`), even though `:on` already
   * established that the root is structurally part of the join key.
   * `cleanupJoinOutput` re-collapses them per join type so the agent
   * sees the plain `root` name post-join.
   */
  readonly shadowedRoots: ReadonlySet<string>;
};

/**
 * Describe an `:on` side's AST shape for {@link JoinOnShapeError}.
 * Returns `{ ok: true }` when the side is a keyword or a `(col …)`
 * reference (the two SSOT shapes); otherwise returns a human-readable
 * label for what was actually received plus an optional "did you mean"
 * keyword form when the receiver is a bare symbol (the most common
 * LLM mistake — they forgot the leading `:`).
 */
const describeOnSideShape = (
  node: unknown,
): { readonly ok: true } | { readonly received: string; readonly didYouMean?: string } => {
  const shape = astShape(node);
  if (shape.kind === 'keyword') {
    return { ok: true };
  }
  if (shape.kind === 'opCall' && shape.call.name === 'col') {
    return { ok: true };
  }
  if (shape.kind === 'symbolRef') {
    return { received: `bare symbol \`${shape.sym}\``, didYouMean: `:${shape.sym}` };
  }
  if (shape.kind === 'opCall') {
    return { received: `\`(${shape.call.name} …)\` call` };
  }
  if (shape.kind === 'vector') {
    return { received: 'a vector' };
  }
  if (shape.kind === 'embedded') {
    const v = shape.value;
    if (typeof v === 'string') {
      return { received: `string \`"${v}"\`` };
    }
    if (v === null || typeof v === 'number' || typeof v === 'boolean') {
      return { received: `${typeof v} \`${String(v)}\`` };
    }
    return { received: 'an embedded value' };
  }
  return { received: shape.kind };
};

/**
 * Validate the shape of one `:on` entry — a single side (self-join)
 * or a `[outer inner]` pair (cross-join). Rather than a Zod union's
 * opaque "Invalid input", raises a targeted {@link JoinOnShapeError} that
 * names the entry index, the side role, what was received, and lists
 * the three valid `:on` shapes as suggestions. A pair of the wrong
 * arity throws a `ShapeError` (structural defect, not a side-shape
 * mismatch).
 */
const validateOnEntry = (entry: unknown, idx: number, ctx: SpecialCtx, onPath: string): void => {
  const entryPath = elemPath(onPath, idx);
  // SSOT: one shape-check + throw per side, parameterised by role and
  // path so the pair and self-join branches share the same body.
  const checkSide = (side: unknown, role: 'outer' | 'inner' | 'self', sidePath: string): void => {
    const check = describeOnSideShape(side);
    if ('received' in check) {
      throw new JoinOnShapeError(idx, role, check.received, nodePos(ctx.spans, sidePath), check.didYouMean);
    }
  };
  if (Array.isArray(entry)) {
    if (entry.length !== 2) {
      throw new ShapeError(
        `:on entry #${idx}: a pair entry must be exactly two sides (\`[outer inner]\`), got ${entry.length}`,
        nodePos(ctx.spans, entryPath),
        ':on',
      );
    }
    checkSide(entry[0], 'outer', elemPath(entryPath, 0));
    checkSide(entry[1], 'inner', elemPath(entryPath, 1));
    return;
  }
  checkSide(entry, 'self', entryPath);
};

/**
 * Walk `:on` and produce arquero-ready key-name pairs. Self-join
 * entries (single side) resolve the SAME side spec against both
 * schemas; cross-join entries (`[outer inner]`) resolve outer against
 * `dsA` and inner against `dsB`. Struct sub-path keys are projected
 * into synthetic scalar columns (collision-safe names) on the
 * appropriate dataset; the synthetic names are listed in the returned
 * `syntheticKeys` set so apply can drop them from the join output.
 *
 * When both sides of an entry resolve to the same *root* column — a
 * struct sub-path key (e.g. both sides `(col wellString externalId)`)
 * or an identity-keyed relation — that root is added to
 * `shadowedRoots`. `cleanupJoinOutput` then de-shadows the
 * post-join collision (`root_1`/`root_2`) back to plain `root` per
 * join type — the SSOT that "the `:on` clause defines what's
 * structurally part of the key; the key doesn't double up in the
 * output" holds for sub-path keys, not just bare-column keys.
 *
 * Per-entry shape validation runs through `validateOnEntry` before
 * `resolveSide` touches the schema — a bare-symbol or non-`(col …)`
 * opCall on either side surfaces a {@link JoinOnShapeError} with the
 * three valid `:on` shapes listed, instead of falling through to a
 * generic "Invalid input" Zod union error.
 */
const resolveOnClause = (
  on: readonly unknown[],
  dsA: MaterializedDataset,
  dsB: MaterializedDataset,
  onPath: string,
  ctx: SpecialCtx,
): ResolvedOn => {
  const resolved = on.map((entry, i) => {
    validateOnEntry(entry, i, ctx, onPath);
    const entryPath = elemPath(onPath, i);
    const entryPos = nodePos(ctx.spans, entryPath);
    const [outerSide, innerSide]: readonly [OnSideShape, OnSideShape] = Array.isArray(entry)
      ? [entry[0] as OnSideShape, entry[1] as OnSideShape]
      : [entry as OnSideShape, entry as OnSideShape];
    const outer = resolveSide(outerSide, 'outer', i, dsA, ctx.identities, entryPos);
    const inner = resolveSide(innerSide, 'inner', i, dsB, ctx.identities, entryPos);
    // An identity key (`identity.key(cell)`) can never equal a scalar key,
    // so a mixed entry would silently match nothing — reject it up front
    // with the sub-path recovery shape instead.
    if ((outer.identityKeyed === true) !== (inner.identityKeyed === true)) {
      const keyed = outer.identityKeyed === true ? outer : inner;
      const keyedRole = outer.identityKeyed === true ? 'outer' : 'inner';
      const scalarRole = outer.identityKeyed === true ? 'inner' : 'outer';
      throw new TypeMismatchError(
        `:on entry #${i}: the ${keyedRole} key \`${keyed.rootColumn}\` is a relation joined by instance identity, but the ${scalarRole} key is a scalar — an identity key never equals a scalar, so this join would match nothing`,
        'identity-keyed relation joined against a scalar key',
        'join',
        entryPos,
        `Join relation against relation directly, or key the relation on a scalar sub-path: \`(col ${keyed.rootColumn} externalId)\`.`,
      );
    }
    return { outer, inner };
  });

  // Fold the per-side synthetic-column augmentations into the two
  // datasets so the arquero join sees one augmented MaterializedDataset
  // per side (rather than threading augmentations through state).
  const augmentedA = withSyntheticKeys(
    dsA,
    resolved.flatMap((r) => (r.outer.synthetic ? [r.outer.synthetic] : [])),
  );
  const augmentedB = withSyntheticKeys(
    dsB,
    resolved.flatMap((r) => (r.inner.synthetic ? [r.inner.synthetic] : [])),
  );
  const syntheticKeys = new Set(
    resolved.flatMap((r) => [
      ...(r.outer.synthetic ? [r.outer.synthetic.name] : []),
      ...(r.inner.synthetic ? [r.inner.synthetic.name] : []),
    ]),
  );
  // A root is "shadowed" iff both sides of the SAME entry resolved
  // their sub-path key against the same struct column. Cross-named
  // pairs (e.g. outer `(col wellString externalId)` ⋈ inner `(col
  // string externalId)`) leave both roots untouched; bare-column keys
  // never have a `rootColumn` and are already handled by arquero's
  // natural key dedup.
  const shadowedRoots = new Set<string>(
    resolved.flatMap((r) =>
      r.outer.rootColumn !== undefined && r.outer.rootColumn === r.inner.rootColumn ? [r.outer.rootColumn] : [],
    ),
  );
  return {
    keyPairs: resolved.map((r) => ({ left: r.outer.keyName, right: r.inner.keyName })),
    augmentedA,
    augmentedB,
    syntheticKeys,
    shadowedRoots,
  };
};

type SideResolution = {
  readonly keyName: string;
  readonly synthetic?: { readonly name: string; readonly cells: readonly Cell[] };
  /**
   * Root column name when this side resolved through a synthetic key —
   * a struct sub-path key (`(col root sub…)` with `sub.length > 0`) or
   * an identity-keyed relation; `undefined` for plain bare-column keys.
   * Used by {@link resolveOnClause} to detect *shadowed roots* — columns
   * that both sides use as their key root and that would otherwise
   * collide post-join as arquero `_1`/`_2` suffixes. Plain bare-column
   * keys leave this `undefined` because arquero's natural key dedup
   * already handles same-named scalar join keys.
   */
  readonly rootColumn?: string;
  /**
   * Set when this side's key is a relation keyed by its row-identity
   * contract (`identity.key` per cell). {@link resolveOnClause} rejects
   * an entry where exactly one side is identity-keyed — an identity key
   * never equals a scalar key, so such a join would silently match
   * nothing.
   */
  readonly identityKeyed?: true;
};

/**
 * The identity contract a bare join-key column is keyed by, if any: the
 * declared identity contract carried on either identity-bearing column kind (a
 * bare `row-identity` or a `hydrated-relation`), or — for an untyped verb-output
 * column — the contract the identity table claims for EVERY non-null runtime
 * cell (mirroring sort's `checkSortable` probe of verb outputs). `undefined`
 * means the column is an ordinary scalar key.
 */
const keyingIdentity = (
  colType: ColumnType | undefined,
  cells: readonly Cell[],
  identities: IdentityTable,
): RowIdentityType | undefined => {
  const unwrapped = colType === undefined ? undefined : unwrapNullable(colType);
  // Either identity-bearing kind keys on its declared identity contract — the
  // single {@link identityElement} SSOT recognises both.
  const declaredIdentity = identityElement(colType);
  if (declaredIdentity !== undefined) {
    return declaredIdentity.identity;
  }
  if (unwrapped !== undefined && unwrapped.kind !== 'unknown') {
    return undefined;
  }
  const present = cells.filter((cell) => cell !== null && cell !== undefined);
  const claimed = present[0] === undefined ? undefined : identities.claimingIdentity(present[0]);
  if (claimed === undefined) {
    return undefined;
  }
  return present.every((cell) => claimed.is(cell)) ? claimed : undefined;
};

/**
 * Resolve a bare (sub-path-less) column key. A struct column has no
 * canonical scalar key → rejected with the sub-path hint. A relation
 * column is keyed by its identity contract — a synthetic per-row
 * `identity.key(cell)` scalar — so relation⋈relation joins match by
 * instance identity instead of cell OBJECT identity (which never matches:
 * every row materialises its own `{space, externalId}` object). A cell the
 * contract doesn't claim keys to `null` rather than failing the join.
 * Everything else joins on the column as-is.
 */
const resolveBareColumn = (
  column: string,
  which: 'outer' | 'inner',
  idx: number,
  dataset: MaterializedDataset,
  identities: IdentityTable,
  pos: SourcePos,
): SideResolution => {
  const colType = dataset.schema.get(column);
  const unwrapped = colType === undefined ? undefined : unwrapNullable(colType);
  if (unwrapped?.kind === 'struct') {
    const subs = scalarSubfields(unwrapped);
    throw new TypeMismatchError(
      `:on ${which} column \`${column}\` is a struct; join on a sub-path with \`(col ${column} ${preferredSubfield(subs)})\``,
      'struct key needs a sub-path',
      'join',
      pos,
      relationColumnHint(column, subs),
    );
  }
  const cells = dataset.columns[column] ?? [];
  const identity = keyingIdentity(colType, cells, identities);
  if (identity === undefined) {
    return { keyName: column };
  }
  const keyed = cells.map((cell) => (identity.is(cell) ? identity.key(cell) : null)) as readonly Cell[];
  const synthName = which === 'outer' ? `__joinkey_a_${idx}` : `__joinkey_b_${idx}`;
  return { keyName: synthName, synthetic: { name: synthName, cells: keyed }, rootColumn: column, identityKeyed: true };
};
/**
 * Resolve one `:on` side against the side's role dataset. A bare
 * keyword names a column directly; a `(col …)` references a sub-path.
 * Bare-keyword name must exist on the role dataset; bare keys resolve
 * through {@link resolveBareColumn} (struct → rejected with a sub-path
 * hint, relation → keyed by instance identity). `(col …)` labels go
 * through the shared `pickColumnPath` SSOT so the
 * binding-name-as-first-label is uniformly ignored.
 *
 * Sub-path keys also carry their root column name out via
 * {@link SideResolution.rootColumn} so {@link resolveOnClause} can
 * detect shadowed roots — columns that both sides use as a key root
 * and that would otherwise collide post-join as arquero `_1`/`_2`
 * suffixes.
 */
const resolveSide = (
  side: OnSideShape,
  which: 'outer' | 'inner',
  idx: number,
  dataset: MaterializedDataset,
  identities: IdentityTable,
  pos: SourcePos,
): SideResolution => {
  if ('$kw' in side) {
    const colName = side.$kw;
    if (!dataset.schema.has(colName)) {
      throw new TypeMismatchError(
        `:on ${which} key \`${colName}\` is not a column on the ${which === 'outer' ? 'ds-a (outer)' : 'ds-b (inner)'} dataset (available: ${[...dataset.schema.keys()].join(', ') || '—'})`,
        'unknown column on the join side',
        'join',
        pos,
      );
    }
    return resolveBareColumn(colName, which, idx, dataset, identities, pos);
  }

  // (col …) form — extract labels via the shared SSOT, walk via
  // pickColumnPath. Strict: a `(col …)` with no labels or any
  // non-literal label is a ShapeError (join `:on` keys must be
  // statically inspectable to drive the join plan).
  const labels = colNodeLabels(side);
  if (labels === undefined || labels.length === 0) {
    throw new ShapeError(
      `:on ${which} \`(col …)\` labels must be keywords, symbols, or string literals — at least one label is required, and a nested expression as a label is not supported inside join :on`,
      pos,
    );
  }
  const picked = pickColumnPath(labels, (n) => dataset.schema.has(n));
  if (picked === undefined) {
    throw new TypeMismatchError(
      `:on ${which} key — none of [${labels.join(' ')}] is a column on the ${which === 'outer' ? 'ds-a (outer)' : 'ds-b (inner)'} dataset (available: ${[...dataset.schema.keys()].join(', ') || '—'})`,
      'no matching column on the join side',
      'join',
      pos,
    );
  }
  const { column, path } = picked;
  if (path.length === 0) {
    return resolveBareColumn(column, which, idx, dataset, identities, pos);
  }
  // Sub-path key → validate, then project to a synthetic scalar key
  // column. Collision-safe synthetic names (`__joinkey_a_<i>` /
  // `__joinkey_b_<i>`); dropped from the output after the join.
  // `rootColumn` is the walked column itself — passed up so
  // `resolveOnClause` can register it as a shadowed root when both
  // sides agree on it (see `cleanupJoinOutput`).
  const colType = dataset.schema.get(column);
  if (colType !== undefined) {
    validatePath(colType, column, path, which, pos);
  }
  const baseCells = dataset.columns[column] ?? [];
  const walked = baseCells.map((cell) => walkStructPath(cell, path)) as readonly Cell[];
  const synthName = which === 'outer' ? `__joinkey_a_${idx}` : `__joinkey_b_${idx}`;
  return { keyName: synthName, synthetic: { name: synthName, cells: walked }, rootColumn: column };
};

/**
 * Validate a struct sub-path against the column's type. An
 * `unknown`-typed column (every `transform` / `aggregate` / `join`
 * output) can't be proven invalid statically, so the walk is allowed
 * and the runtime path-walk handles a genuine mismatch (null).
 */
const validatePath = (
  colType: ColumnType,
  column: string,
  path: readonly string[],
  which: string,
  pos: SourcePos,
): void => {
  const walk = (t: ColumnType, remaining: readonly string[]): void => {
    if (remaining.length === 0 || t.kind === 'unknown') {
      return;
    }
    const [seg, ...rest] = remaining as readonly [string, ...string[]];
    const next = structField(t, column, seg, which, pos);
    walk(next, rest);
  };
  walk(unwrapNullable(colType), path);
};

const structField = (t: ColumnType, column: string, seg: string, which: string, pos: SourcePos): ColumnType => {
  const fields = walkableFields(t);
  if (fields === undefined) {
    throw new TypeMismatchError(
      `:on ${which} key \`${column}\`: sub-path segment \`${seg}\` — \`${column}\` has no walkable subfields (not a struct or relation reference)`,
      'sub-path on a column without walkable subfields',
      'join',
      pos,
    );
  }
  const next = fields[seg];
  if (next === undefined) {
    throw new TypeMismatchError(
      `:on ${which} key \`${column}\` has no sub-field \`${seg}\` (available: ${Object.keys(fields).join(', ') || '—'})`,
      'no such struct sub-field',
      'join',
      pos,
    );
  }
  return unwrapNullable(next);
};

/**
 * Augment a dataset with synthetic struct-key columns produced by
 * `resolveSide`. Each entry is folded into the dataset's columns + a
 * matching `unknownCol` schema entry. Returns the dataset unchanged
 * when there are no synthetics to add.
 */
const withSyntheticKeys = (
  ds: MaterializedDataset,
  synths: readonly { readonly name: string; readonly cells: readonly Cell[] }[],
): MaterializedDataset => {
  if (synths.length === 0) {
    return ds;
  }
  const augmentedCols: Record<string, readonly Cell[]> = {
    ...ds.columns,
    ...Object.fromEntries(synths.map((s) => [s.name, s.cells])),
  };
  const augmentedSchema = schemaFromEntries([...ds.schema, ...synths.map((s) => [s.name, unknownCol] as const)]);
  // Adding synthetic key columns leaves the row set unchanged, so the
  // identity axis passes through — crucial on the anchor (left) side so
  // `runtimeJoin`'s row-index carry can regather it across the join.
  return new MaterializedDataset(augmentedCols, augmentedSchema, ds.identity);
};
/**
 * Per-`JoinType` shadow-dedup function. Each rewrites the mutable
 * working `cols` map and `entries` schema array in place; the caller
 * ({@link cleanupJoinOutput}) constructs the final
 * `MaterializedDataset` once at the end so depth-validation runs on
 * the final shape, never on intermediate states.
 */
type ShadowDedupeFn = (cols: Record<string, readonly Cell[]>, entries: [string, ColumnType][], root: string) => void;

/**
 * Fold two suffixed columns into one — write `combinedCells` under
 * `targetName`, drop both originals, rename `keep`'s schema entry to
 * `targetName` and remove `drop`'s. SSOT for the rewrite mechanics so
 * the dispatch arms ({@link renameSuffixedAndDropOther},
 * {@link coalesceSuffixedPair}) only declare *which* cells survive
 * and what name they go under — never how the cols/entries rewrite is
 * performed.
 */
const foldSuffixedColumns = (
  cols: Record<string, readonly Cell[]>,
  entries: [string, ColumnType][],
  keep: string,
  drop: string,
  targetName: string,
  combinedCells: readonly Cell[],
): void => {
  cols[targetName] = combinedCells;
  delete cols[keep];
  delete cols[drop];
  const keepIdx = entries.findIndex(([k]) => k === keep);
  const keepEntry = keepIdx >= 0 ? entries[keepIdx] : undefined;
  if (keepEntry !== undefined) {
    entries[keepIdx] = [targetName, keepEntry[1]];
  }
  const dropIdx = entries.findIndex(([k]) => k === drop);
  if (dropIdx >= 0) {
    entries.splice(dropIdx, 1);
  }
};

/**
 * Build a shadow-dedup function for the asymmetric join types
 * (`:inner`/`:left` keep the outer's column; `:right` keeps the
 * inner's). The surviving suffixed column's cells become the target;
 * the other is dropped.
 */
const renameSuffixedAndDropOther =
  (keepSuffix: 1 | 2): ShadowDedupeFn =>
  (cols, entries, root) => {
    const a = `${root}_1`;
    const b = `${root}_2`;
    // Pre-trimmed sides (e.g. `(columns inner [:string :well])`) may
    // not produce both suffixes — bail out silently; no shadow to dedup.
    if (cols[a] === undefined || cols[b] === undefined) {
      return;
    }
    const [keep, drop] = keepSuffix === 1 ? [a, b] : [b, a];
    foldSuffixedColumns(cols, entries, keep, drop, root, cols[keep] as readonly Cell[]);
  };

/**
 * `:full` shadow-dedup: synthesize the root column per row by
 * preferring the outer cell, falling back to the inner when outer is
 * null/undefined. Matched rows take the outer's struct; outer-only
 * rows take the outer's; inner-only rows take the inner's. Type is
 * inherited from the outer's schema entry (the outer is the primary by
 * convention; for the canonical iPSO instance-ref use case the two
 * structs share the same shape).
 */
const coalesceSuffixedPair: ShadowDedupeFn = (cols, entries, root) => {
  const a = `${root}_1`;
  const b = `${root}_2`;
  if (cols[a] === undefined || cols[b] === undefined) {
    return;
  }
  const aCells = cols[a] as readonly Cell[];
  const bCells = cols[b] as readonly Cell[];
  const merged = aCells.map((av, i): Cell => (av !== null && av !== undefined ? av : (bCells[i] ?? null)));
  foldSuffixedColumns(cols, entries, a, b, root, merged);
};

/**
 * Per-`JoinType` dispatch for de-shadowing a struct-root column after
 * the arquero join. Parallel to `JOIN_FNS` in `runtime/arquero.ts`
 * (which dispatches the actual join call); this table dispatches the
 * engine-side post-join cleanup. {@link JOIN_TYPES} stays the SSOT for
 * the join-type variants — both tables derive from it, so adding a
 * variant is a single edit to that constant plus a TS-enforced update
 * here and in `JOIN_FNS`.
 */
const JOIN_SHADOW_DEDUPE: Record<JoinType, ShadowDedupeFn> = {
  inner: renameSuffixedAndDropOther(1),
  left: renameSuffixedAndDropOther(1),
  right: renameSuffixedAndDropOther(2),
  full: coalesceSuffixedPair,
  // Cross has no key entries, so no struct root is ever shadowed; this entry
  // is never invoked. Mirror inner for totality.
  cross: renameSuffixedAndDropOther(1),
};

/**
 * Single post-join cleanup pass. Drops synthetic struct-key columns
 * AND de-shadows struct-root columns where both sides used the same
 * root as a sub-path key. Mutates working `cols`/`entries` in place;
 * constructs the final `MaterializedDataset` exactly once so depth-
 * validation runs on the final shape, never on intermediate states.
 *
 * Implements the SSOT invariant promised by the join op: "the `:on`
 * clause defines what's structurally part of the key; the key doesn't
 * double up in the output." Bare-column keys get this for free from
 * arquero's natural dedup; sub-path keys get it from
 * {@link JOIN_SHADOW_DEDUPE}.
 */
const cleanupJoinOutput = (
  joined: MaterializedDataset,
  resolved: ResolvedOn,
  joinType: JoinType,
): MaterializedDataset => {
  if (resolved.syntheticKeys.size === 0 && resolved.shadowedRoots.size === 0) {
    return joined;
  }
  const cols: Record<string, readonly Cell[]> = { ...joined.columns };
  resolved.syntheticKeys.forEach((k) => {
    delete cols[k];
  });
  const entries: [string, ColumnType][] = [...joined.schema].filter(([k]) => !resolved.syntheticKeys.has(k));
  const dedupe = JOIN_SHADOW_DEDUPE[joinType];
  resolved.shadowedRoots.forEach((root) => {
    dedupe(cols, entries, root);
  });
  // Dropping synthetic key columns and de-shadowing struct roots leaves
  // the row set unchanged, so the joined identity (already synthesised by
  // `runtimeJoin`'s anchor row-index carry) passes through.
  return new MaterializedDataset(cols, schemaFromEntries(entries), joined.identity);
};
/**
 * Source side a left-sourced column is null-filled on per join type
 * (its rows are absent when the join keeps the OTHER side's unmatched
 * rows). A left-sourced column gains nullability on `right`/`full`; a
 * right-sourced column on `left`/`full`. `inner` fills neither side.
 */
const LEFT_NULL_FILLED: Record<JoinType, boolean> = {
  inner: false,
  left: false,
  right: true,
  full: true,
  // Cross is a cartesian product: every left row pairs with every right row,
  // so no side has unmatched (null-filled) rows.
  cross: false,
};
const RIGHT_NULL_FILLED: Record<JoinType, boolean> = {
  inner: false,
  left: true,
  right: false,
  full: true,
  cross: false,
};

/**
 * Which inherent operand a de-shadowed struct root is sourced from per
 * join type: `inner`/`left`/`full` keep the OUTER (`dsA`) column type,
 * `right` keeps the INNER (`dsB`). (`:full` coalesces the VALUES per row
 * but the surviving type is the outer's.) Parallel to
 * {@link JOIN_SHADOW_DEDUPE}, which collapses the corresponding cells.
 */
const SHADOW_ROOT_SOURCE: Record<JoinType, 'left' | 'right'> = {
  inner: 'left',
  left: 'left',
  right: 'right',
  full: 'left',
  cross: 'left',
};

/**
 * Re-type the join's emitted columns from the two INHERENT operand
 * schemas (`dsA.schema` / `dsB.schema`), recovering the DECLARED types the
 * value-based `refineSchema` cannot reconstruct — relation / struct / enum
 * / integer kinds and the `storedUnit`/`targetUnit` slugs riding on the
 * leaf. The `unit` dimension itself is deliberately NOT recovered from the
 * operands: units are value-level (boxed cells), and the refined
 * post-cleanup type (`current`) already carries the cell-derived unit —
 * {@link replaceUnit} keeps exactly that one on the sourced type.
 *
 * Mirrors what `cleanupJoinOutput` actually produced: iterates the real
 * post-cleanup `output.schema` keys (preserving their order) and maps
 * each back to its source column on the correct side —
 *
 *   - `name_1` whose base is on `dsA` → outer collision side.
 *   - `name_2` whose base is on `dsB` → inner collision side.
 *   - a de-shadowed struct root (in `resolved.shadowedRoots`) → outer
 *     for `inner`/`left`/`full`, inner for `right`.
 *   - a name present on BOTH sides (a deduped bare join key, kept once
 *     under its plain name) → outer.
 *   - else present on exactly one side → that side.
 *   - else → keep the post-cleanup type as-is (safe fallback).
 *
 * Null-fill nullability is then applied per join type via
 * {@link LEFT_NULL_FILLED} / {@link RIGHT_NULL_FILLED}; `nullableCol` is
 * idempotent and preserves the leaf's metadata (the unit / stored-unit
 * accessors unwrap the nullable wrapper). A same-named
 * (deduped) join key is exempt — its
 * single column carries the shared key on every output row, so it is never
 * null-filled even on right/full joins.
 */
const typeJoinedColumns = (
  output: MaterializedDataset,
  dsA: MaterializedDataset,
  dsB: MaterializedDataset,
  joinType: JoinType,
  resolved: ResolvedOn,
): Schema => {
  const classify = (
    name: string,
    current: ColumnType,
  ): { readonly type: ColumnType; readonly side?: 'left' | 'right' } => {
    if (resolved.shadowedRoots.has(name)) {
      const side = SHADOW_ROOT_SOURCE[joinType];
      const sourced = (side === 'left' ? dsA.schema : dsB.schema).get(name);
      return sourced === undefined ? { type: current } : { type: sourced, side };
    }
    if (name.endsWith('_1')) {
      const sourced = dsA.schema.get(name.slice(0, -2));
      if (sourced !== undefined) {
        return { type: sourced, side: 'left' };
      }
    }
    if (name.endsWith('_2')) {
      const sourced = dsB.schema.get(name.slice(0, -2));
      if (sourced !== undefined) {
        return { type: sourced, side: 'right' };
      }
    }
    const inA = dsA.schema.get(name);
    const inB = dsB.schema.get(name);
    if (inA !== undefined) {
      // Present on both (deduped bare join key) or left-only — left-sourced.
      return { type: inA, side: 'left' };
    }
    if (inB !== undefined) {
      return { type: inB, side: 'right' };
    }
    return { type: current };
  };

  const nullFilled: Record<'left' | 'right', boolean> = {
    left: LEFT_NULL_FILLED[joinType],
    right: RIGHT_NULL_FILLED[joinType],
  };

  // A same-named (deduped) join key is the single column carrying the shared
  // key value, present on EVERY output row — so it is never null-filled, even
  // on right/full joins. (A cross-named outer key keeps its own column, which
  // IS null on unmatched rows, so it stays subject to the per-side rule.)
  const alwaysPresentKeys = new Set(resolved.keyPairs.filter((p) => p.left === p.right).map((p) => p.left));

  return schemaFromEntries(
    [...output.schema].map(([name, current]): readonly [string, ColumnType] => {
      const { type, side } = classify(name, current);
      // Declared kinds + slug metadata from the source; the unit dimension
      // from the cell-refined post-cleanup type.
      const retyped = replaceUnit(type, unitOfType(current));
      const nullable = side !== undefined && nullFilled[side] && !alwaysPresentKeys.has(name);
      return [name, nullable ? nullableCol(retyped) : retyped];
    }),
  );
};
